import { root, computed }                 from 'rewire-core';
import {
  IGrid,
  createGrid,
  createColumn,
  IColumn,
  IGridOptions,
}                                         from 'rewire-grid';
import {
  IValidationRule,
  IUdf,
  IValidationRuleSchema,
}                                         from '../DataTypes';
import DataMutations                      from '../graphql/Mutations';
import DataQueries                        from '../graphql/Queries';
import { notifications }                  from '../ui/Notifications';
import udfsSubscriptionService            from '../services/UdfsSubscriptionService';
import AddValidationRuleDialogModel       from './AddValidationRuleDialogModel';
import mainPageModel                      from '../main-navigation/MainPageModel';
import SettingsModel                      from './SettingsModel';
import ValidationRuleEditorInfoPanelModel from './ValidationRuleEditorInfoPanelModel';
import ValidationsRuleEditor              from './ValidationsRuleEditor';

export interface IValidationRuleTable {
  id: string;
  name: string;
  schema: IValidationRuleSchema[];
  rules: IValidationRule[];
  selected?: boolean;
}

export default class ValidationRuleEditorModel extends SettingsModel<{table?: IValidationRuleTable}> {
  protected _rules:                            () => IValidationRule[];
  readonly grid:                               IGrid;
  readonly validationRuleEditorInfoPanelModel: ValidationRuleEditorInfoPanelModel;
  readonly addValidationRuleDialogModel:       AddValidationRuleDialogModel;

  constructor() {
    super('Validations');
    this.SettingsView                       = ValidationsRuleEditor;
    this.grid                               = this.createGrid();
    this.validationRuleEditorInfoPanelModel = new ValidationRuleEditorInfoPanelModel(this);
    this.addValidationRuleDialogModel       = new AddValidationRuleDialogModel(this);
    this.grid.rowHeight                     = 38;
    this.grid.headerRowHeight               = 28;

    root((_dispose) => {
      this._rules   = computed(() => this.tables.map(t => t.rules.length), this.rulesComputation, []);
    });

    return this;
  }

  protected rulesComputation = () => {
    if (!this.tables.length) return [] as IValidationRule[];
    const rules = this.tables.reduce((prev: IValidationRule[], curr: IValidationRuleTable) => {
      prev.push(...curr.rules.filter((r: IValidationRule) => !r.isNew));
      return prev;
    }, []);
    return rules;
  };

  reset() {
    this.table = undefined;
    this.validationRuleEditorInfoPanelModel._reset();
    this.addValidationRuleDialogModel.reset();
  }

  async load() {
    this.reset();
    const validationRules = await DataQueries.loadValidationRules();
    if (!validationRules) {
      this.setItems([]);
      return;
    }

    const ruleTableMap = new Map<string, IValidationRuleTable>();
    validationRules.schema.forEach((schema: IValidationRule) => {
      const validationRuleTable = ruleTableMap.get(schema.category);
      if (!validationRuleTable) {
        const newValidationRuleTable: IValidationRuleTable = {
          id: schema.category,
          name: schema.category,
          schema: [schema],
          rules: [],
        };
        ruleTableMap.set(schema.category, newValidationRuleTable);
      } else {
        validationRuleTable.schema.push(schema);
      }
    });

    validationRules.rules.forEach((rule: IValidationRule) => {
      const validationRuleTable = ruleTableMap.get(rule.category);
      if (validationRuleTable) {
        validationRuleTable.rules.push(rule);
      }
    });

    const ruleTables: IValidationRuleTable[] = Array.from(ruleTableMap.values());

    this.setItems(ruleTables);
  }

  get access(): boolean {
    return mainPageModel.validationRulesAccess;
  }

  get viewOnly(): boolean {
    return (!this._canAddItem && this.selectedRule?.isNew) || (!this._canEditItem && !this.selectedRule?.isNew);
  }

  get _canAddItem(): boolean {
    return mainPageModel.canEditValidationRules;
  }

  get _canEditItem(): boolean {
    return mainPageModel.canEditValidationRules;
  }

  get _canDeleteItem(): boolean {
    return mainPageModel.canEditValidationRules;
  }

  get udfs(): IUdf[] {
    return udfsSubscriptionService.subscriptionModel.getUdfsByDocumentType('employees');
  }

  get titleText() {
    return `${this.title} ${this.selectedItem ? ' - ' + this.selectedItem.name : ''}`;
  }

  get tables(): IValidationRuleTable[] {
    return this.state.items;
  }

  get table(): IValidationRuleTable | undefined {
    return this.state.table;
  }

  set table(value: IValidationRuleTable | undefined) {
    this.state.table = value;
  }

  get availableUdfs(): IUdf[] {
    return this.udfs;
  }

  get rules(): IValidationRule[] {
    return this._rules();
  }

  get selectedRule(): IValidationRule | undefined {
    return this.grid.selectedRows[0]?.data;
  }

  showAddRuleDialog() {
    if (!this.table) {
      return;
    }
    this.addValidationRuleDialogModel.load();
  }

  addRule(rule: IValidationRule) {
    const addedRule         = this.grid.insertRowAtSelection({id: rule.id, data: rule});
    const addedRuleNameCell = addedRule.cells['name'];
    this.grid.selectCells([addedRuleNameCell]);
    setTimeout(() => addedRuleNameCell.setFocus(), 0);
  }

  createGrid() {
    const cols: IColumn[] = [];

    cols.push(createColumn('name', 'Validation Rule', {type: 'text'}));

    const gridOptions: IGridOptions = {
      readOnly: true,
      clearSelectionOnBlur: false,
    };

    const grid = createGrid([], cols, gridOptions);
    return grid;
  }

  loadGrid() {
    const rows = this.table?.rules ?? [];
    this.grid.fixedRows[0]!.cells['name'].value = this.table?.name ?? 'Validation Rule';
    if (!this.table) {
      this.grid.set([]);
    } else {
      this.grid.set(rows.map((r) => ({id: r.id, data: r})));
    }
  }

  async select(selectedItemId?: string | null): Promise<void> {
    this.isLoading = true;
    if (!this._settingsState.selected) {
      await this.load();
      this.activate();
      this._settingsState.selected = true;
    }

    this.updateTitle();
    if ((!selectedItemId && !this.selectedItem) || (selectedItemId && (this.selectedItem && this.selectedItem.id === selectedItemId))) {
      this.isLoading = false;
      return;
    }
    this.grid.clearSelection();
    const table  = selectedItemId ? this.tables.find(t => t.id === selectedItemId) : undefined;
    this.table = table;
    this._selectItem(table);
    this.loadGrid();
    this.isLoading = false;
  }

  deactivate(): void {}
  activate(): void {}

  async deleteRule(rule: IValidationRule): Promise<boolean> {
    if (rule.isNew) {
      this.grid.removeSelectedRows();
      return true;
    }

    const ruleIdx = this.table?.rules?.findIndex((r: IValidationRule) => r.id === rule.id) ?? -1;
    if (ruleIdx < 0) {
      return false;
    }

    this.table!.rules.splice(ruleIdx, 1);
    const result = await DataMutations.saveValidationRules(this.rules);
    if (result) {
      this.grid.removeSelectedRows();
      notifications.success(`${this.table!.name} rule has been successfully deleted`);
      return true;
    } else {
      notifications.error(`${this.table!.name} rule has failed to be deleted`);
      this.table!.rules.splice(ruleIdx, 0, rule);
      return false;
    }
  }

  async saveRule(rule: IValidationRule): Promise<boolean> {
    const isNew = rule.isNew;
    if (isNew) {
      delete rule.isNew;
      this.table!.rules.push(rule);
    }

    const result = await DataMutations.saveValidationRules(this.rules);
    if (result) {
      notifications.success(`${this.table!.name} rule has been successfully saved`);
      return true;
    } else {
      notifications.error(`${this.table!.name} rule has failed to be saved`);
      if (isNew) {
        const newRule    = this.table!.rules.pop();
        newRule!.isNew = true;
      }
      return false;
    }
  }
}
